export const FULL_OWNERSHIP = 'FULL_OWNERSHIP'
export const USUFRUCT = 'USUFRUCT'
export const BARE_OWNERSHIP = 'BARE_OWNERSHIP'

export const OWNERSHIP_RIGHT_OPTIONS = [
  { value: FULL_OWNERSHIP, label: 'Volle eigendom' },
  { value: USUFRUCT, label: 'Vruchtgebruik' },
  { value: BARE_OWNERSHIP, label: 'Naakte eigendom' },
  { value: 'EMPHYTEUSIS_GRANTOR', label: 'Erfpachtgever' },
  { value: 'EMPHYTEUSIS_HOLDER', label: 'Erfpachthouder' },
  { value: 'SUPERFICIES_GRANTOR', label: 'Opstalgever' },
  { value: 'SUPERFICIES_HOLDER', label: 'Opstalhouder' },
]

const RIGHT_LABELS = Object.fromEntries(OWNERSHIP_RIGHT_OPTIONS.map(option => [option.value, option.label]))

const LEGACY_RIGHT_TYPES = {
  'volle eigendom': FULL_OWNERSHIP,
  'volledige eigendom': FULL_OWNERSHIP,
  'full ownership': FULL_OWNERSHIP,
  'vrucht gebruik': USUFRUCT,
  vruchtgebruik: USUFRUCT,
  usufruct: USUFRUCT,
  'naakte eigendom': BARE_OWNERSHIP,
  'blote eigendom': BARE_OWNERSHIP,
  'bare ownership': BARE_OWNERSHIP,
  erfpachtgever: 'EMPHYTEUSIS_GRANTOR',
  erfpachthouder: 'EMPHYTEUSIS_HOLDER',
  opstalgever: 'SUPERFICIES_GRANTOR',
  opstalhouder: 'SUPERFICIES_HOLDER',
}

const makeId = prefix => {
  const value = typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`
  return `${prefix}-${value}`
}

const toNumber = value => {
  if (value === null || value === undefined || value === '') return 0
  const parsed = Number(String(value).replace(',', '.'))
  return Number.isFinite(parsed) ? parsed : 0
}

export const formatOwnershipPercentage = (value, maximumFractionDigits = 6) => new Intl.NumberFormat('nl-BE', {
  minimumFractionDigits: 0,
  maximumFractionDigits,
}).format(toNumber(value))

export const getRightLabel = type => RIGHT_LABELS[type] || type

export const canonicalOwnershipType = value => {
  if (!value) return FULL_OWNERSHIP
  if (RIGHT_LABELS[value]) return value
  const normalized = String(value).trim().toLowerCase()
  return LEGACY_RIGHT_TYPES[normalized] || normalized.toUpperCase().replaceAll(' ', '_')
}

export const createHolder = (party, sharePercentage = '100') => ({
  id: makeId('holder'),
  partyType: party.type === 'company' ? 'company' : 'contact',
  partyId: party.id,
  party: { ...party },
  sharePercentage,
})

export const createRight = (type = FULL_OWNERSHIP, valuePercentage = '100', holders = []) => ({
  id: makeId('right'),
  type,
  valuePercentage,
  endDate: null,
  valuationMethod: 'manual',
  valuationNote: '',
  holders,
})

export const createPortion = (sharePercentage = '100') => ({
  id: makeId('portion'),
  sharePercentage,
  structureType: 'FULL',
  rights: [createRight()],
})

export const createEmptyOwnershipPlan = () => ({
  version: 1,
  portions: [createPortion()],
})

export const hasOwnershipHolders = plan => Boolean(
  plan?.portions?.some(portion => (
    portion?.rights?.some(right => right?.holders?.length)
  )),
)

const normalizeParty = (holder, partyType, partyId) => {
  const rawParty = holder.party || holder.contact || holder.company || {}
  return {
    ...rawParty,
    id: rawParty.id ?? partyId,
    type: partyType,
  }
}

export const normalizeOwnershipPlan = plan => {
  if (!plan?.portions?.length) return createEmptyOwnershipPlan()
  return {
    version: 1,
    portions: plan.portions.map(portion => ({
      id: portion.id || makeId('portion'),
      sharePercentage: String(portion.sharePercentage ?? portion.share_percentage ?? ''),
      structureType: String(portion.structureType ?? portion.structure_type ?? 'FULL').toUpperCase(),
      rights: (portion.rights || []).map(right => ({
        id: right.id || makeId('right'),
        type: canonicalOwnershipType(right.type ?? right.ownership_type),
        valuePercentage: String(right.valuePercentage ?? right.value_percentage ?? ''),
        endDate: right.endDate || right.ownership_right_end_date
          ? String(right.endDate ?? right.ownership_right_end_date).slice(0, 10)
          : null,
        valuationMethod: right.valuationMethod ?? right.valuation_method ?? 'manual',
        valuationNote: right.valuationNote ?? right.valuation_note ?? '',
        holders: (right.holders || []).map(holder => {
          const partyType = holder.partyType ?? holder.party_type ?? (holder.company ? 'company' : 'contact')
          const partyId = holder.partyId ?? holder.party_id ?? holder.party?.id ?? holder.contact?.id ?? holder.company?.id
          return {
            id: holder.id || makeId('holder'),
            partyType,
            partyId,
            party: normalizeParty(holder, partyType, partyId),
            sharePercentage: String(holder.sharePercentage ?? holder.share_percentage ?? ''),
          }
        }),
      })),
    })),
  }
}

const participantParty = participant => ({
  ...participant,
  id: participant.id,
  type: participant.type === 'company' ? 'company' : 'contact',
})

export const legacyParticipantsToOwnershipPlan = participants => {
  if (!participants?.length) return createEmptyOwnershipPlan()

  const groups = new Map()
  participants.forEach(participant => {
    const type = canonicalOwnershipType(participant.ownership_type)
    if (!groups.has(type)) groups.set(type, [])
    groups.get(type).push(participant)
  })

  const isSimpleFullOwnership = groups.size === 1 && groups.has(FULL_OWNERSHIP)
  const rights = [...groups.entries()].map(([type, group]) => {
    const groupTotal = group.reduce((sum, participant) => sum + toNumber(participant.ownership_percentage), 0)
    const holders = group.map(participant => createHolder(
      participantParty(participant),
      isSimpleFullOwnership
        ? String(toNumber(participant.ownership_percentage))
        : groupTotal > 0
          ? String((toNumber(participant.ownership_percentage) / groupTotal) * 100)
          : String(100 / group.length),
    ))
    return createRight(type, isSimpleFullOwnership ? '100' : String(groupTotal), holders)
  })

  return {
    version: 1,
    portions: [{
      id: makeId('portion'),
      sharePercentage: '100',
      structureType: isSimpleFullOwnership ? 'FULL' : 'SPLIT',
      rights,
    }],
  }
}

export const evenShares = count => {
  if (count <= 0) return []
  const base = Number((100 / count).toFixed(6))
  return Array.from({ length: count }, (_, index) => (
    index === count - 1
      ? String(Number((100 - (base * (count - 1))).toFixed(6)))
      : String(base)
  ))
}

export const rebalanceHolders = holders => {
  const shares = evenShares(holders.length)
  return holders.map((holder, index) => ({ ...holder, sharePercentage: shares[index] }))
}

export const calculateOwnershipSummary = plan => {
  const summaries = new Map()
  ;(plan?.portions || []).forEach(portion => {
    const portionShare = toNumber(portion.sharePercentage)
    ;(portion.rights || []).forEach(right => {
      const valueShare = toNumber(right.valuePercentage)
      ;(right.holders || []).forEach(holder => {
        const key = `${holder.partyType}:${holder.partyId}`
        if (!summaries.has(key)) {
          summaries.set(key, {
            key,
            partyType: holder.partyType,
            partyId: holder.partyId,
            party: holder.party,
            invoicePercentage: 0,
            holdings: [],
            rightTypes: new Set(),
            endDates: new Set(),
          })
        }
        const holderShare = toNumber(holder.sharePercentage)
        const summary = summaries.get(key)
        summary.invoicePercentage += portionShare * valueShare * holderShare / 10000
        summary.rightTypes.add(right.type)
        if (right.endDate) summary.endDates.add(right.endDate)

        if (right.type === FULL_OWNERSHIP) {
          const legalShare = portionShare * holderShare / 100
          summary.holdings.push(`${formatOwnershipPercentage(legalShare)}% volle eigendom`)
        } else {
          summary.holdings.push(
            `${formatOwnershipPercentage(holderShare)}% van ${getRightLabel(right.type).toLowerCase()} op ${formatOwnershipPercentage(portionShare)}% van pand`,
          )
        }
      })
    })
  })
  return [...summaries.values()]
}

export const convertOwnershipPlanToSimple = plan => {
  const holders = calculateOwnershipSummary(plan).map(summary => createHolder({
    ...(summary.party || {}),
    id: summary.partyId,
    type: summary.partyType,
  }, String(summary.invoicePercentage)))

  return {
    version: 1,
    portions: [{
      ...createPortion('100'),
      rights: [createRight(FULL_OWNERSHIP, '100', holders)],
    }],
  }
}

export const ownershipPlanToLegacyParticipants = plan => calculateOwnershipSummary(plan).map(summary => ({
  ...summary.party,
  id: summary.partyId,
  type: summary.partyType,
  ownership_percentage: String(summary.invoicePercentage),
  ownership_type: summary.rightTypes.size === 1
    ? getRightLabel([...summary.rightTypes][0]).toLowerCase()
    : 'gemengde rechten',
  ownership_right_end_date: summary.endDates.size === 1 ? [...summary.endDates][0] : null,
}))

const nearlyHundred = value => Math.abs(value - 100) <= 0.000001

export const validateOwnershipPlan = plan => {
  const errors = []
  const portions = plan?.portions || []
  if (!portions.length) return { valid: false, errors: ['Add at least one ownership part.'] }

  const portionTotal = portions.reduce((sum, portion) => sum + toNumber(portion.sharePercentage), 0)
  portions.forEach((portion, portionIndex) => {
    const portionNumber = portionIndex + 1
    const portionShare = toNumber(portion.sharePercentage)
    if (portionShare <= 0 || portionShare > 100) {
      errors.push(`Ownership part ${portionNumber} must be greater than 0% and at most 100%.`)
    }

    if (!portion.rights?.length) {
      errors.push(`Ownership part ${portionNumber} needs an ownership right.`)
      return
    }

    const valueTotal = portion.rights.reduce((sum, right) => sum + toNumber(right.valuePercentage), 0)
    if (!nearlyHundred(valueTotal)) {
      errors.push(`Right values in ownership part ${portionNumber} total ${formatOwnershipPercentage(valueTotal)}%; expected 100%.`)
    }

    if (portion.structureType === 'FULL' && (
      portion.rights.length !== 1 || portion.rights[0].type !== FULL_OWNERSHIP
    )) {
      errors.push(`Ownership part ${portionNumber} must contain one full ownership right.`)
    }
    if (portion.structureType === 'SPLIT' && portion.rights.length < 2) {
      errors.push(`Ownership part ${portionNumber} needs at least two ownership rights.`)
    }

    const rightTypes = new Set()
    portion.rights.forEach(right => {
      const rightValue = toNumber(right.valuePercentage)
      if (!RIGHT_LABELS[right.type]) {
        errors.push(`Ownership part ${portionNumber} contains an unknown ownership right.`)
      }
      if (rightTypes.has(right.type)) {
        errors.push(`${getRightLabel(right.type)} can only appear once in ownership part ${portionNumber}.`)
      }
      rightTypes.add(right.type)
      if (portion.structureType === 'SPLIT' && right.type === FULL_OWNERSHIP) {
        errors.push(`Split ownership part ${portionNumber} cannot contain full ownership.`)
      }
      if (rightValue <= 0 || rightValue > 100) {
        errors.push(`${getRightLabel(right.type)} value in ownership part ${portionNumber} must be greater than 0% and at most 100%.`)
      }
      if (!right.holders?.length) {
        errors.push(`Assign a holder to ${getRightLabel(right.type).toLowerCase()} in ownership part ${portionNumber}.`)
        return
      }
      const holderTotal = right.holders.reduce((sum, holder) => sum + toNumber(holder.sharePercentage), 0)
      if (!nearlyHundred(holderTotal)) {
        errors.push(`${getRightLabel(right.type)} holders in ownership part ${portionNumber} total ${formatOwnershipPercentage(holderTotal)}%; expected 100%.`)
      }
      const holderKeys = new Set()
      right.holders.forEach(holder => {
        const holderShare = toNumber(holder.sharePercentage)
        const key = `${holder.partyType}:${holder.partyId}`
        if (!['contact', 'company'].includes(holder.partyType) || holder.partyId === null || holder.partyId === undefined || holder.partyId === '') {
          errors.push(`${getRightLabel(right.type)} in ownership part ${portionNumber} has an invalid holder.`)
        }
        if (holderKeys.has(key)) {
          errors.push(`A party can only appear once within ${getRightLabel(right.type).toLowerCase()} in ownership part ${portionNumber}.`)
        }
        holderKeys.add(key)
        if (holderShare <= 0 || holderShare > 100) {
          errors.push(`Holder shares in ${getRightLabel(right.type).toLowerCase()} must be greater than 0% and at most 100%.`)
        }
      })
    })
  })

  if (!nearlyHundred(portionTotal)) {
    errors.unshift(`Ownership parts total ${formatOwnershipPercentage(portionTotal)}%; expected 100%.`)
  }

  const summary = calculateOwnershipSummary(plan)
  const invoiceTotal = summary.reduce((sum, item) => sum + item.invoicePercentage, 0)
  if (summary.length && !nearlyHundred(invoiceTotal)) {
    errors.push(`Calculated economic allocation totals ${formatOwnershipPercentage(invoiceTotal)}%; expected 100%.`)
  }

  return { valid: errors.length === 0, errors, portionTotal, invoiceTotal }
}
